import { useEffect, useRef } from "react";
import { useQuotesWatchlistStore } from "./store";
import { useSymbolsStore } from "@/vybe-trader-client/symbols";
import { useAccountsStore } from "@/lib/accounts/store";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols";
import { useOpenedTradesStore } from "@/vybe-trader-client/opened-trades";
import { generateAccountKey } from "@/vybe-trader-client/chart/utils/accountHelpers";

export const initializeQuotesWatchlistStore = () => {
  const loadWatchlist = useQuotesWatchlistStore.getState().loadWatchlist;
  loadWatchlist();
};

export const useQuotesWatchlistSync = () => {
  const symbolsLoadingStatus = useSymbolsStore((state) => state.loadingStatus);
  const symbolsByCurrency = useSymbolsStore((state) => state.byCurrency);
  const activeAccountId = useAccountsStore((state) => state.activeAccountId);
  const accounts = useAccountsStore((state) => state.accounts);
  const syncWithAvailableSymbols = useQuotesWatchlistStore(
    (state) => state.syncWithAvailableSymbols
  );
  const resetWatchlist = useQuotesWatchlistStore(
    (state) => state.resetWatchlist
  );
  const resetSubscribedSymbols = useSubscribedSymbolsStore(
    (state) => state.reset
  );

  // Track current account key to detect changes
  const currentAccountKeyRef = useRef<string | null>(null);

  // Reset watchlist, subscribed symbols, and clear chart store when ACCOUNT KEY changes
  useEffect(() => {
    const newAccountKey = generateAccountKey(activeAccountId, accounts);
    const previousAccountKey = currentAccountKeyRef.current;

    // Only reset if the account key actually changed
    if (previousAccountKey !== newAccountKey) {
      // Update the ref with new key
      currentAccountKeyRef.current = newAccountKey;

      // ...but NOT on the first resolve. generateAccountKey returns null until the accounts store holds both
      // an active id and the matching account, so on boot this fires once as null → the active account. That
      // is not a switch, and there is no previous account's state to clear.
      //
      // It became destructive once the watchlist started syncing on the FIRST catalog batch instead of the
      // last: that batch now lands before the accounts store resolves, so the subscribe happens first and
      // this reset then wiped it — every row rendered and no price ever arrived. Previously the complete
      // catalog always landed after accounts, so the reset ran first and cost nothing.
      if (previousAccountKey !== null) {
        resetWatchlist();
        resetSubscribedSymbols();
      }
    }
  }, [activeAccountId, accounts, resetWatchlist, resetSubscribedSymbols]);

  // Sync as soon as ANY symbols have arrived, and again when the catalog completes.
  //
  // This used to wait for `complete`. The catalog streams in batches and a large account carries thousands
  // of symbols, so the watchlist — and, because subscription happens inside the sync, every live price —
  // waited for the last batch: ~15s of "No symbols in watchlist" and then 0.00000 rows, for eight symbols
  // that were in the first batch. Syncing on partial data resolves and SUBSCRIBES those rows immediately;
  // the sync is idempotent and only treats the complete catalog as authoritative (see the store).
  useEffect(() => {
    if (symbolsByCurrency.size === 0) return;
    syncWithAvailableSymbols(
      symbolsByCurrency,
      symbolsLoadingStatus === "complete"
    );
  }, [symbolsLoadingStatus, symbolsByCurrency, syncWithAvailableSymbols]);
};
